/**
 * Role classification utilities — shared between backend and frontend.
 *
 * Pure functions for categorizing and filtering user roles.
 * No framework dependencies. No UI concerns.
 *
 * UI-specific display helpers (MEMBER_ROLE, getDisplayRoles, getSingleRoleBadgeColor, etc.)
 * live in @insio/ui-toolkit — see libs/ui-toolkit/src/utils/role-display.ts
 */
import { UserRole } from '../enums/user-role';

/**
 * Roles that can be assigned via UI (invite / edit user).
 * Non-assignable roles are excluded — they can only be assigned directly in the database.
 */
export const ASSIGNABLE_ROLES: readonly UserRole[] = [
  'ADMIN',
  'ACCOUNTANT',
  'APPROVER',
  'RULES_MANAGER',
  'CODE_LISTS_MANAGER'
] as const;

/** Roles of a platform (AMS) operator: they grant the operator console and `@SystemRolesOnly`. */
export const PLATFORM_OPERATOR_ROLES = [
  'SYSTEM_BILLING_ADMIN',
  'SYSTEM_SUPPORT_ADMIN'
] as const satisfies readonly UserRole[];

export type PlatformOperatorRole = (typeof PLATFORM_OPERATOR_ROLES)[number];

/**
 * Roles never assigned via UI or API, only displayed; an edit of a person's or a key's roles keeps
 * them.
 */
export const NON_ASSIGNABLE_ROLES: readonly UserRole[] = [
  'SECURITY_ADMIN',
  ...PLATFORM_OPERATOR_ROLES
] as const;

/** Roles an API key's identity may hold: every assignable role except ADMIN. */
export const KEY_ASSIGNABLE_ROLES: readonly UserRole[] = ASSIGNABLE_ROLES.filter(
  role => role !== UserRole.ADMIN
);

export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];
export type NonAssignableRole = (typeof NON_ASSIGNABLE_ROLES)[number];

/** Check whether a role string is a non-assignable role. */
export function isNonAssignableRole(role: string): role is NonAssignableRole {
  return (NON_ASSIGNABLE_ROLES as readonly string[]).includes(role);
}

/** Check whether a role string is a platform operator role. */
export function isPlatformOperatorRole(role: string): role is PlatformOperatorRole {
  return (PLATFORM_OPERATOR_ROLES as readonly string[]).includes(role);
}

/** Extract all non-assignable roles from a roles array. */
export function getNonAssignableRoles(roles: readonly string[]): NonAssignableRole[] {
  return roles.filter(isNonAssignableRole);
}

/**
 * Extract all assignable roles from a roles array.
 * Returns empty array when no assignable roles are present (user is baseline MEMBER).
 */
export function getAssignableRoles(roles: readonly string[]): AssignableRole[] {
  return roles.filter((r): r is AssignableRole =>
    (ASSIGNABLE_ROLES as readonly string[]).includes(r)
  );
}
